// de strings for messages/library.ts. Same keys and {variables} as en there.
// German has no {of} form, so {category} always sits in front of "KI-Stimmen"
// ("Anime KI-Stimmen", "Japanische KI-Stimmen"), the way people search.
// Category names are written to work there: nouns as modifiers ("Promi",
// "Film"), languages and nationalities as plural adjectives ("Japanische").
// Avoid putting {category} after an article that changes the ending
// ("der", "keine").
import type { Dict } from "../index";

const library: Dict = {
  "library.of": "{name}",
  "library.ofLanguage": "{name}",

  "library.seo.title": "KI-Stimmen-Bibliothek{filter} | Jammable",
  "library.seo.titleFiltered": "{filter} KI-Stimmen: Bibliothek | Jammable",
  "library.seo.description":
    "Entdecke über 93.000 KI-Stimmen. Erstelle KI-Cover mit Stimmen aus Anime, Gaming, Cartoons, K-Pop und mehr. Jeden Tag kommen neue Stimmen dazu.",
  "library.seo.ogDescription":
    "Entdecke über 93.000 KI-Stimmen. Erstelle KI-Cover mit Stimmen aus Anime, Gaming, Cartoons, K-Pop und mehr.",
  "library.seo.listDescription":
    "Entdecke über 93.000 KI-Stimmen. Erstelle KI-Cover mit Stimmen aus Anime, Gaming, Cartoons, K-Pop und mehr. Jeden Tag kommen neue Stimmen dazu.",
  "library.seo.listDescriptionFiltered":
    "{filter} KI-Stimmen auf Jammable. Erstelle KI-Cover mit {filter} Stimmen oder nutze Text zu Sprache. Über 93.000 KI-Stimmen in der Bibliothek.",
  "library.crumb.home": "Startseite",
  "library.crumb.library": "KI-Stimmen-Bibliothek",
  "library.crumb.filter": "{category} KI-Stimmen",

  "library.title": "Stimmen-Bibliothek",
  "library.subtitle":
    "Über 93.000 Stimmen. Tippe auf eine, um sie anzuhören, oder erstelle direkt ein Cover.",
  "library.train": "Trainiere deine eigene Stimme",
  "library.search": "Stimmen suchen",
  "library.searchIn": "{categoryLower} KI-Stimmen suchen",
  "library.categoriesNav": "Stimmen-Kategorien",
  "library.all": "Alle",
  "library.moreCategories": "Mehr Kategorien",
  "library.fewerCategories": "Weniger Kategorien",
  "library.collections": "Sammlungen",
  "library.collections.prev": "Vorherige Sammlungen",
  "library.collections.next": "Weitere Sammlungen",
  "library.collection.voices": "{n} Stimmen",
  "library.voicesIn": "{category} KI-Stimmen",
  "library.allVoices": "Alle Stimmen",
  "library.empty.title": "Keine Stimmen gefunden",
  "library.empty.body":
    "Probier einen anderen Namen oder trainiere die Stimme selbst.",
  "library.loadMore": "Mehr Stimmen laden",

  "library.sort.aria": "Stimmen sortieren",
  "library.sort.hot": "Heute angesagt",
  "library.sort.hot.short": "Angesagt",
  "library.sort.top": "Meistgenutzt",
  "library.sort.top.short": "Meistgenutzt",
  "library.sort.liked": "Beliebteste",
  "library.sort.liked.short": "Beliebteste",
  "library.sort.new": "Neueste",
  "library.sort.new.short": "Neueste",

  "library.hub.title":
    "{category} KI-Stimmen – KI-Cover mit {category} Stimmen erstellen | Jammable",
  "library.hub.titlePage": "{category} KI-Stimmen, Seite {page} | Jammable",
  "library.hub.description":
    "Entdecke über {count} {category} KI-Stimmen auf Jammable. Wähle eine Stimme, um ein KI-Cover zu erstellen oder den KI-Stimmenverzerrer zu nutzen.",
  "library.hub.descriptionPage":
    "Seite {page} von {lastPage}: {category} KI-Stimmen auf Jammable, sortiert nach Nutzung. Wähle eine Stimme, um ein KI-Cover zu erstellen oder den KI-Stimmenverzerrer zu nutzen.",
  "library.hub.name": "{category} KI-Stimmen",
  "library.hub.crumbVoices": "KI-Stimmen",
  "library.hub.voices": "Stimmen",
  "library.hub.page": "Seite {page}",
  "library.hub.h1": "{category} KI-Stimmen",
  "library.hub.intro":
    "Entdecke über {count} {category} KI-Stimmen. Wähle eine aus, um ein KI-Cover zu erstellen, oder nutze sie als Stimmenverzerrer.",
  "library.hub.popular": "Beliebte {categoryLower} KI-Stimmen",
  "library.hub.mostUsedPage":
    "Meistgenutzte {categoryLower} KI-Stimmen, Seite {page} von {lastPage}",
  "library.hub.noMatch": "Keine Treffer in {categoryLower} KI-Stimmen.",
  "library.hub.openLibrary": "In der Stimmen-Bibliothek öffnen",
  "library.hub.moreCategories": "Weitere Kategorien entdecken",
  "library.hub.faq": "Häufige Fragen",
  "library.hub.keepExploring": "Weiter entdecken",
  "library.hub.moreWays": "Mehr Möglichkeiten, mit Jammable kreativ zu werden.",
  "library.hub.link.aiCover": "KI-Cover-Generator",
  "library.hub.link.voiceChanger": "KI-Stimmenverzerrer",
  "library.hub.link.allVoices": "Alle KI-Stimmen ansehen",
  "library.hub.pages": "Seiten",
  "library.hub.first": "Erste",
  "library.hub.prev": "Zurück",
  "library.hub.next": "Weiter",
  "library.hub.last": "Letzte",
  "library.hub.faq1.q": "Was sind {category} KI-Stimmen?",
  "library.hub.faq1.a":
    "{category} KI-Stimmen sind Stimmmodelle, mit denen du KI-Cover erstellen oder deine eigene Stimme verändern kannst. Jammable hat über {count} {categoryLower} KI-Stimmen, und jeden Tag kommen neue dazu.",
  "library.hub.faq2.q": "Wie nutze ich eine {category} KI-Stimme?",
  "library.hub.faq2.a":
    "Öffne eine {category} Stimme, füge Audio hinzu, für das du die Rechte hast (oder nimm dich selbst auf), und klick auf Generieren. Jammable wandelt den Gesang in diese Stimme um und liefert dir in Sekunden ein fertiges Cover.",
  "library.hub.faq3.q": "Wie viele {category} Stimmen gibt es?",
  "library.hub.faq3.a":
    "Jammable hat aktuell über {count} {category} KI-Stimmen, und die Bibliothek wächst täglich, weil die Community neue Stimmen hochlädt und trainiert.",

  "library.cat.public-figure": "Promi",
  "library.cat.music": "Musik",
  "library.cat.cartoon": "Cartoon",
  "library.cat.gaming": "Gaming",
  "library.cat.fictional": "Fiktive",
  "library.cat.anime": "Anime",
  "library.cat.influencer": "Influencer",
  "library.cat.singer": "Sänger",
  "library.cat.comedy": "Comedy",
  "library.cat.pop": "Pop",
  "library.cat.youtuber": "YouTuber",
  "library.cat.tv-show": "TV-Serien",
  "library.cat.rap": "Rap",
  "library.cat.fictional-character": "Figuren",
  "library.cat.movie": "Film",
  "library.cat.movies": "Film",
  "library.cat.funny": "Lustige",
  "library.cat.kpop": "K-Pop",
  "library.cat.k-pop": "K-Pop",
  "library.cat.parody": "Parodie",
  "library.cat.animation": "Animation",
  "library.cat.animated": "Animierte",
  "library.cat.rock": "Rock",
  "library.cat.vtuber": "VTuber",
  "library.cat.fantasy": "Fantasy",
  "library.cat.game": "Videospiel",
  "library.cat.entertainment": "Entertainment",
  "library.cat.rapper": "Rapper",
  "library.cat.horror": "Horror",
  "library.cat.actor": "Schauspieler",
  "library.cat.actors": "Schauspieler",
  "library.cat.actress": "Schauspielerinnen",
  "library.cat.meme": "Meme",
  "library.cat.action": "Action",
  "library.cat.educational": "Lehrreiche",
  "library.cat.hip-hop-rap": "Hip-Hop/Rap",
  "library.cat.hip-hop": "Hip-Hop",
  "library.cat.adventure": "Abenteuer",
  "library.cat.tv": "TV",
  "library.cat.female": "Weibliche",
  "library.cat.male": "Männliche",
  "library.cat.superhero": "Superhelden",
  "library.cat.drama": "Drama",
  "library.cat.sci-fi": "Sci-Fi",
  "library.cat.science-fiction": "Science-Fiction",
  "library.cat.alternative": "Alternative",
  "library.cat.metal": "Metal",
  "library.cat.technology": "Technik",
  "library.cat.indie": "Indie",
  "library.cat.celebrity": "Promi",
  "library.cat.fiction": "Fiktion",
  "library.cat.american": "Amerikanische",
  "library.cat.british": "Britische",
  "library.cat.australian": "Australische",
  "library.cat.latino": "Latino",
  "library.cat.mexico": "Mexikanische",
  "library.cat.villain": "Bösewicht",
  "library.cat.politics": "Politik",
  "library.cat.president": "Präsidenten",
  "library.cat.animal": "Tier",
  "library.cat.animals": "Tier",
  "library.cat.dog": "Hunde",
  "library.cat.cat": "Katzen",
  "library.cat.sports": "Sport",
  "library.cat.wrestling": "Wrestling",
  "library.cat.streamer": "Streamer",
  "library.cat.country": "Country",
  "library.cat.robot": "Roboter",
  "library.cat.sound-effects": "Soundeffekt",
  "library.cat.historical": "Historische",
  "library.cat.folk": "Folk",
  "library.cat.r-b": "R&B",
  "library.cat.rnb": "R&B",
  "library.cat.jazz": "Jazz",
  "library.cat.reggaeton": "Reggaeton",
  "library.cat.electronic": "Electro",
  "library.cat.pop-music": "Pop",
  "library.cat.comic": "Comic",
  "library.cat.demon": "Dämonen",
  "library.cat.news": "Nachrichten",
  "library.cat.artist": "Künstler",
  "library.cat.artistas": "Künstler",
  "library.cat.cantantes": "Sänger",
  "library.cat.cantante": "Sänger",
  "library.cat.personajes": "Figuren",
  "library.cat.character": "Figuren",
  "library.cat.singing": "Gesang",
  "library.cat.mystery": "Mystery",
  "library.cat.christmas": "Weihnachten",
  "library.cat.sitcom": "Sitcom",
  "library.cat.pirate": "Piraten",
  "library.cat.toy": "Spielzeug",
  "library.cat.classic": "Klassische",
  "library.cat.art": "Kunst",
  "library.cat.poetry": "Poesie",
  "library.cat.food": "Food",
  "library.cat.fashion": "Fashion",
  "library.cat.family": "Familien",
  "library.cat.podcast": "Podcast",
  "library.cat.radio": "Radio",
  "library.cat.ai": "KI",
  "library.cat.artificial-intelligence": "KI",
  "library.cat.song": "Song",
  "library.cat.ringtone": "Klingelton",
  "library.cat.web-series": "Webserien",
  "library.cat.idol": "Idol",
  "library.cat.dub": "Synchron",
  "library.cat.80s": "80er",
  "library.cat.indie-games": "Indie-Game",
  "library.cat.game-show": "Gameshow",
  "library.lang.japanese": "Japanische",
  "library.lang.japenese": "Japanische",
  "library.lang.korean": "Koreanische",
  "library.lang.spanish": "Spanische",
  "library.lang.english": "Englische",
  "library.lang.french": "Französische",
  "library.lang.italian": "Italienische",
  "library.lang.german": "Deutsche",
  "library.lang.deutsch": "Deutsche",
  "library.lang.russian": "Russische",
  "library.lang.hebrew": "Hebräische",
  "library.lang.chinese": "Chinesische",
  "library.lang.arabic": "Arabische",
  "library.lang.swedish": "Schwedische",
  "library.lang.polish": "Polnische",
  "library.lang.turkish": "Türkische",
  "library.lang.finnish": "Finnische",
  "library.lang.greek": "Griechische",
  "library.lang.indonesian": "Indonesische",
  "library.lang.thai": "Thailändische",
  "library.lang.hungarian": "Ungarische",
  "library.lang.filipino": "Philippinische",
  "library.lang.malaysian": "Malaiische",
  "library.lang.portuguese": "Portugiesische",
  "library.lang.brasileiro": "Brasilianische",
  "library.lang.pt-br": "Brasilianische",
  "library.lang.romanian": "Rumänische",
  "library.lang.hindi": "Hindi",
  "library.lang.dutch": "Niederländische",
  "library.lang.czech": "Tschechische",
  "library.lang.vietnamese": "Vietnamesische",
};

export default library;
